import { useState, useEffect } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, 
  DollarSign, 
  Star,
  TrendingUp,
  Clock,
  Video,
  User,
  Settings,
  LogOut,
  Edit,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Users,
  Download,
  FileText,
  Play,
  Wallet,
  Pencil,
  ExternalLink,
  MessageSquare,
  Save,
  Loader2,
  Phone,
  Headphones,
  Bell,
  CalendarPlus,
  Send,
  Check,
  X,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ExpertDashboardPage() {
  const navigate = useNavigate();
  const location = useRouterLocation();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming"); // "upcoming" or "past"
  const [expandedSession, setExpandedSession] = useState(null);
  const [expandedUpcoming, setExpandedUpcoming] = useState(null);
  const [downloadingRecording, setDownloadingRecording] = useState(null);
  const [downloadingTranscript, setDownloadingTranscript] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [slotRequests, setSlotRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [proposingSlots, setProposingSlots] = useState(false);
  const [proposedSlots, setProposedSlots] = useState([]);
  const [expertMessage, setExpertMessage] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("UTC");
  const [selectedProposalDate, setSelectedProposalDate] = useState(null);
  
  // Cancel and Reschedule states
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleMessage, setRescheduleMessage] = useState("");
  const [proposingReschedule, setProposingReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);

  // Timezone options
  const TIMEZONES = [
    { value: "UTC", label: "UTC" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "Europe/London", label: "London (GMT)" },
    { value: "Europe/Paris", label: "Paris (CET)" },
    { value: "Europe/Berlin", label: "Berlin (CET)" },
    { value: "Asia/Dubai", label: "Dubai (GST)" },
    { value: "Asia/Kolkata", label: "India (IST)" },
    { value: "Asia/Singapore", label: "Singapore (SGT)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Australia/Sydney", label: "Sydney (AEDT)" },
  ];

  useEffect(() => {
    fetchDashboard();
    fetchSlotRequests();
    
    // Check for calendar connection callback
    const params = new URLSearchParams(location.search);
    const calendarStatus = params.get('calendar');
    
    if (calendarStatus === 'connected') {
      toast.success("Google Calendar connected successfully!");
      // Clean up URL
      navigate('/expert-dashboard', { replace: true });
    } else if (calendarStatus === 'error') {
      const message = params.get('message') || 'Unknown error';
      toast.error(`Failed to connect Google Calendar: ${message}`);
      // Clean up URL
      navigate('/expert-dashboard', { replace: true });
    }
  }, [location.search]);

  const fetchSlotRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API}/expert/slot-requests`);
      setSlotRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching slot requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleProposeSlots = async (requestId) => {
    if (proposedSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }
    
    setProposingSlots(true);
    try {
      await axios.post(`${API}/slot-requests/${requestId}/propose`, {
        proposed_slots: proposedSlots,
        message: expertMessage
      });
      toast.success("Slots proposed successfully! The user has been notified.");
      setSelectedRequest(null);
      setProposedSlots([]);
      setExpertMessage("");
      fetchSlotRequests();
    } catch (error) {
      console.error("Error proposing slots:", error);
      toast.error("Failed to propose slots");
    } finally {
      setProposingSlots(false);
    }
  };

  const addProposedSlot = (date, time, time12h) => {
    const slot = { date, time, time_12h: time12h };
    if (proposedSlots.length >= 6) {
      toast.error("Maximum 6 slots allowed");
      return;
    }
    if (!proposedSlots.find(s => s.date === date && s.time === time)) {
      setProposedSlots([...proposedSlots, slot]);
    }
  };

  const removeProposedSlot = (index) => {
    setProposedSlots(proposedSlots.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-500/20", text: "text-yellow-500", label: "Pending" },
      slots_proposed: { bg: "bg-blue-500/20", text: "text-blue-500", label: "Slots Sent" },
      accepted: { bg: "bg-green-500/20", text: "text-green-500", label: "Accepted" },
      payment_pending: { bg: "bg-orange-500/20", text: "text-orange-500", label: "Payment Pending" },
      more_requested: { bg: "bg-orange-500/20", text: "text-orange-500", label: "More Requested" }
    };
    return badges[status] || badges.pending;
  };

  // Cancel booking handler (expert cancels - full refund to user)
  const handleCancelBooking = async () => {
    if (!cancelModal) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/bookings/${cancelModal.id}/cancel`, {
        reason: cancelReason
      });
      
      toast.success("Booking cancelled. A full refund has been issued to the client.");
      setCancelModal(null);
      setCancelReason("");
      fetchDashboard();
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setCancelling(false);
    }
  };

  // Request user to reschedule (just notifies user)
  const handleRequestUserReschedule = async (booking) => {
    try {
      await axios.post(`${API}/bookings/${booking.id}/reschedule/request`, {
        reason: "Expert requested reschedule"
      });
      toast.success("Reschedule request sent! The client will be notified to select a new time.");
      fetchDashboard();
    } catch (error) {
      console.error("Error requesting reschedule:", error);
      toast.error(error.response?.data?.detail || "Failed to request reschedule");
    }
  };

  // Propose reschedule slots
  const handleProposeRescheduleSlots = async () => {
    if (!rescheduleModal || rescheduleSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }
    
    setProposingReschedule(true);
    try {
      await axios.post(`${API}/bookings/${rescheduleModal.id}/reschedule/propose`, {
        proposed_slots: rescheduleSlots,
        message: rescheduleMessage
      });
      toast.success("Reschedule slots sent! The client will be notified.");
      setRescheduleModal(null);
      setRescheduleSlots([]);
      setRescheduleMessage("");
      setRescheduleDate(null);
      fetchDashboard();
    } catch (error) {
      console.error("Error proposing reschedule:", error);
      toast.error(error.response?.data?.detail || "Failed to propose reschedule slots");
    } finally {
      setProposingReschedule(false);
    }
  };

  const addRescheduleSlot = (date, time, time12h) => {
    const slot = { date, time, time_12h: time12h };
    if (rescheduleSlots.length >= 6) {
      toast.error("Maximum 6 slots allowed");
      return;
    }
    if (!rescheduleSlots.find(s => s.date === date && s.time === time)) {
      setRescheduleSlots([...rescheduleSlots, slot]);
    }
  };

  const removeRescheduleSlot = (index) => {
    setRescheduleSlots(rescheduleSlots.filter((_, i) => i !== index));
  };

  // Generate available dates (next 14 days, excluding weekends)
  const generateAvailableDates = () => {
    const dates = [];
    const now = new Date();
    for (let day = 1; day <= 14; day++) {
      const date = new Date(now);
      date.setDate(date.getDate() + day);
      if (date.getDay() === 0 || date.getDay() === 6) continue; // Skip weekends
      
      dates.push({
        date: date.toISOString().split('T')[0],
        displayDate: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    return dates;
  };

  // Generate time slots for a selected date
  const generateTimeSlotsForDate = (dateStr) => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const time12h = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ date: dateStr, time, time_12h: time12h });
      }
    }
    return slots;
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/experts/me/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 404) {
        navigate("/become-expert");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const toggleAvailability = async () => {
    try {
      await axios.put(`${API}/experts/me`, {
        available: !dashboard.expert.available
      });
      setDashboard(prev => ({
        ...prev,
        expert: { ...prev.expert, available: !prev.expert.available }
      }));
      toast.success(dashboard.expert.available ? "You are now unavailable" : "You are now available");
    } catch (error) {
      toast.error("Failed to update availability");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500/20 text-green-500";
      case "pending":
        return "bg-yellow-500/20 text-yellow-500";
      case "cancelled":
        return "bg-red-500/20 text-red-500";
      case "completed":
        return "bg-blue-500/20 text-blue-500";
      default:
        return "bg-slate-500/20 text-slate-500";
    }
  };

  const handleDownloadRecording = async (bookingId) => {
    setDownloadingRecording(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/recording`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("expertbase_token")}` }
      });
      const { download_url, filename } = response.data;
      const link = document.createElement('a');
      link.href = download_url;
      link.setAttribute('download', filename || `session_${bookingId}_recording.mp4`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Recording download started");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(error.response?.data?.detail || "Recording not available for this session");
      } else {
        toast.error("Failed to download recording");
      }
    } finally {
      setDownloadingRecording(null);
    }
  };

  const handleDownloadTranscript = async (bookingId) => {
    setDownloadingTranscript(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/transcript`);
      const blob = new Blob([response.data.transcript], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `session_${bookingId}_transcript.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Transcript downloaded successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Transcript not available for this session");
      } else {
        toast.error("Failed to download transcript");
      }
    } finally {
      setDownloadingTranscript(null);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleSaveNotes = async (bookingId) => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/bookings/${bookingId}/notes`, {
        expert_notes: notesText
      });
      // Update local state
      setDashboard(prev => ({
        ...prev,
        bookings: prev.bookings.map(b => 
          b.id === bookingId ? { ...b, expert_notes: notesText } : b
        )
      }));
      setEditingNotes(null);
      toast.success("Notes saved successfully");
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const startEditingNotes = (booking) => {
    setEditingNotes(booking.id);
    setNotesText(booking.expert_notes || "");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const { expert, bookings, stats } = dashboard;
  
  const upcomingBookings = bookings.filter(b => 
    (b.status === "confirmed" || b.status === "pending") && 
    new Date(b.scheduled_date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.payment_status === "paid" && 
    (b.status === "completed" || new Date(b.scheduled_date) < new Date())
  );

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <section className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div 
                className="bg-charcoal border border-slate-800 p-6 rounded-sm sticky top-28"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {/* Profile Card */}
                <div className="text-center mb-6 pb-6 border-b border-slate-800">
                  <div className="w-20 h-20 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-display text-2xl text-gold-500">
                      {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                  <h3 className="font-display text-xl">{expert.name}</h3>
                  <p className="text-sm text-slate-500">{expert.title}</p>
                  
                  {/* Availability Toggle */}
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <Switch
                      checked={expert.available}
                      onCheckedChange={toggleAvailability}
                      data-testid="availability-toggle"
                    />
                    <Label className={expert.available ? "text-green-500" : "text-slate-500"}>
                      {expert.available ? "Available" : "Unavailable"}
                    </Label>
                  </div>
                </div>

                <nav className="space-y-2">
                  <button className="w-full flex items-center gap-3 px-4 py-3 bg-gold-500/10 text-gold-500 rounded-sm">
                    <TrendingUp className="w-5 h-5" />
                    Dashboard
                  </button>
                  <button 
                    onClick={() => {
                      const el = document.getElementById('slot-requests-section');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
                    data-testid="slot-requests-btn"
                  >
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5" />
                      Slot Requests
                    </div>
                    {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length > 0 && (
                      <span className="bg-gold-500 text-obsidian text-xs font-bold px-2 py-0.5 rounded-full">
                        {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => navigate("/expert/edit-profile")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-5 h-5" />
                    Edit Profile
                  </button>
                  <button 
                    onClick={() => navigate(`/experts/${expert.id}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
                  >
                    <User className="w-5 h-5" />
                    View Public Profile
                  </button>
                  <a 
                    href="tel:+971558945236"
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-gold-500 hover:bg-gold-500/10 rounded-sm transition-colors"
                    data-testid="sidebar-support-btn"
                  >
                    <Headphones className="w-5 h-5" />
                    Support
                  </a>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </nav>
                
                {/* Support Card */}
                <div className="mt-6 pt-6 border-t border-slate-800">
                  <div className="bg-gold-500/10 border border-gold-500/20 rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-gold-500" />
                      <span className="text-sm font-medium text-gold-500">Expert Support</span>
                    </div>
                    <a 
                      href="tel:+971558945236" 
                      className="text-white hover:text-gold-400 transition-colors font-medium"
                    >
                      +971 55 894 5236
                    </a>
                    <p className="text-xs text-slate-500 mt-1">Priority support for experts</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="font-display text-4xl mb-2">Expert Dashboard</h1>
                    <p className="text-slate-400">Welcome back, {expert.name.split(' ')[0]}</p>
                  </div>
                  {expert.verified && (
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 text-gold-500 rounded-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      Verified Expert
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-gold-500" />
                    <span className="text-slate-400 text-sm">Total Earnings</span>
                  </div>
                  <p className="font-display text-3xl text-gold-500">${stats.total_earnings.toFixed(0)}</p>
                </div>
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Video className="w-5 h-5 text-blue-500" />
                    <span className="text-slate-400 text-sm">Total Sessions</span>
                  </div>
                  <p className="font-display text-3xl">{stats.total_sessions}</p>
                </div>
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="text-slate-400 text-sm">Rating</span>
                  </div>
                  <p className="font-display text-3xl">{stats.rating}</p>
                </div>
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    <span className="text-slate-400 text-sm">Pending</span>
                  </div>
                  <p className="font-display text-3xl">{stats.pending_bookings}</p>
                </div>
              </motion.div>

              {/* Earnings Card */}
              <motion.div
                className="bg-gradient-to-r from-gold-500/20 to-gold-500/5 border border-gold-500/30 p-6 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gold-500/20 rounded-full flex items-center justify-center">
                      <Wallet className="w-7 h-7 text-gold-500" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Your Hourly Rate</p>
                      <p className="font-display text-4xl text-gold-500">${expert.hourly_rate}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm mb-1">This Month</p>
                    <p className="font-display text-2xl text-white">
                      ${pastBookings.filter(b => {
                        const bookingDate = new Date(b.scheduled_date);
                        const now = new Date();
                        return bookingDate.getMonth() === now.getMonth() && 
                               bookingDate.getFullYear() === now.getFullYear();
                      }).reduce((sum, b) => sum + (b.amount * 0.8), 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-slate-500">After platform fee</p>
                  </div>
                </div>
              </motion.div>

              {/* Tabs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex gap-4 mb-6 border-b border-slate-800">
                  <button
                    onClick={() => setActiveTab("upcoming")}
                    className={`pb-4 px-2 font-medium transition-colors relative ${
                      activeTab === "upcoming" 
                        ? "text-gold-500" 
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Upcoming Sessions ({upcomingBookings.length})
                    {activeTab === "upcoming" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("past")}
                    className={`pb-4 px-2 font-medium transition-colors relative ${
                      activeTab === "past" 
                        ? "text-gold-500" 
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Past Sessions ({pastBookings.length})
                    {activeTab === "past" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500" />
                    )}
                  </button>
                </div>

                {/* Upcoming Sessions Tab */}
                {activeTab === "upcoming" && (
                  <>
                    {upcomingBookings.length > 0 ? (
                      <div className="space-y-4">
                        {upcomingBookings.map((booking) => (
                          <div 
                            key={booking.id}
                            className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden"
                          >
                            {/* Session Header - Clickable */}
                            <div 
                              className="p-6 cursor-pointer hover:bg-charcoal/80 transition-colors"
                              onClick={() => setExpandedUpcoming(expandedUpcoming === booking.id ? null : booking.id)}
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-slate-400" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold">Session with Client</h3>
                                    <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {booking.scheduled_time}
                                      </span>
                                      <span>{booking.duration_minutes} min</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-gold-500 font-semibold">${booking.amount}</span>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                                    {booking.status}
                                  </span>
                                  {expandedUpcoming === booking.id ? (
                                    <ChevronUp className="w-5 h-5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {expandedUpcoming === booking.id && (
                              <div className="border-t border-slate-800 p-6 bg-obsidian/50">
                                {/* Session Details Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                  <div className="flex items-center gap-3">
                                    <Calendar className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Date & Time</p>
                                      <p className="font-medium">
                                        {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                          weekday: 'long',
                                          month: 'long',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                      <p className="text-sm text-slate-400">{booking.scheduled_time}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Duration</p>
                                      <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <DollarSign className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Your Earnings</p>
                                      <p className="font-medium">${(booking.amount * 0.8).toFixed(0)}</p>
                                      <p className="text-xs text-slate-500">After 20% platform fee</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Topic/Notes from Client */}
                                {booking.notes && (
                                  <div className="mb-6 p-4 bg-charcoal rounded-sm border border-slate-700">
                                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-2">
                                      <MessageSquare className="w-4 h-4" />
                                      Topic / Client's Notes
                                    </p>
                                    <p className="text-slate-300">{booking.notes}</p>
                                  </div>
                                )}

                                {/* Expert's Notes */}
                                <div className="mb-6">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-slate-500 flex items-center gap-2">
                                      <FileText className="w-4 h-4" />
                                      Your Notes
                                    </p>
                                    {editingNotes !== booking.id && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditingNotes(booking)}
                                        className="text-gold-500 hover:text-gold-400"
                                      >
                                        <Pencil className="w-4 h-4 mr-1" />
                                        {booking.expert_notes ? "Edit" : "Add Notes"}
                                      </Button>
                                    )}
                                  </div>
                                  
                                  {editingNotes === booking.id ? (
                                    <div className="space-y-3">
                                      <Textarea
                                        value={notesText}
                                        onChange={(e) => setNotesText(e.target.value)}
                                        placeholder="Add your preparation notes, talking points, or any relevant information..."
                                        className="bg-charcoal border-slate-700 min-h-[100px]"
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleSaveNotes(booking.id)}
                                          disabled={savingNotes}
                                          className="bg-gold-500 hover:bg-gold-400 text-black"
                                        >
                                          {savingNotes ? (
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                          ) : (
                                            <Save className="w-4 h-4 mr-1" />
                                          )}
                                          Save Notes
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setEditingNotes(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="p-4 bg-charcoal rounded-sm border border-slate-700">
                                      {booking.expert_notes ? (
                                        <p className="text-slate-300 whitespace-pre-wrap">{booking.expert_notes}</p>
                                      ) : (
                                        <p className="text-slate-500 italic">No notes yet. Click "Add Notes" to prepare for this session.</p>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Join Meeting Button */}
                                <div className="flex flex-wrap gap-3">
                                  {booking.meeting_link || booking.daily_room_name ? (
                                    <Button
                                      onClick={() => navigate(`/meeting/${booking.id}`)}
                                      className="bg-green-600 hover:bg-green-500 text-white"
                                      data-testid={`join-meeting-${booking.id}`}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Join Video Call
                                    </Button>
                                  ) : booking.status === 'confirmed' && booking.payment_status === 'paid' ? (
                                    <Button
                                      onClick={() => navigate(`/meeting/${booking.id}`)}
                                      className="bg-green-600 hover:bg-green-500 text-white"
                                      data-testid={`join-meeting-${booking.id}`}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Join Video Call
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      className="border-slate-700"
                                      onClick={() => navigate("/expert/edit-profile")}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Setup Meeting
                                    </Button>
                                  )}
                                  
                                  {/* Reschedule Button */}
                                  {booking.status !== 'cancelled' && !booking.reschedule_status && (
                                    <>
                                      <Button
                                        variant="outline"
                                        className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                                        onClick={() => handleRequestUserReschedule(booking)}
                                        data-testid={`request-reschedule-${booking.id}`}
                                      >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Ask Client to Reschedule
                                      </Button>
                                      <Button
                                        variant="outline"
                                        className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                                        onClick={() => setRescheduleModal(booking)}
                                        data-testid={`propose-reschedule-${booking.id}`}
                                      >
                                        <CalendarPlus className="w-4 h-4 mr-2" />
                                        Propose New Times
                                      </Button>
                                    </>
                                  )}
                                  
                                  {/* Reschedule status */}
                                  {booking.reschedule_status === 'pending' && (
                                    <span className="px-3 py-2 rounded bg-yellow-500/10 text-yellow-500 text-sm">
                                      Waiting for client to select new slot
                                    </span>
                                  )}
                                  {booking.reschedule_status === 'slots_proposed' && (
                                    <span className="px-3 py-2 rounded bg-blue-500/10 text-blue-500 text-sm">
                                      Slots sent - awaiting client selection
                                    </span>
                                  )}
                                  
                                  {/* Cancel Button */}
                                  {booking.status !== 'cancelled' && (
                                    <Button
                                      variant="outline"
                                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                      onClick={() => setCancelModal(booking)}
                                      data-testid={`cancel-${booking.id}`}
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Cancel Session
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-charcoal border border-slate-800 p-12 rounded-sm text-center">
                        <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">No upcoming sessions yet</p>
                        <p className="text-sm text-slate-500 mt-2">Share your profile to get bookings</p>
                      </div>
                    )}
                  </>
                )}

                {/* Past Sessions Tab */}
                {activeTab === "past" && (
                  <>
                    {pastBookings.length > 0 ? (
                      <div className="space-y-4">
                        {pastBookings.map((booking) => (
                          <div 
                            key={booking.id}
                            className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden"
                          >
                            {/* Session Header */}
                            <div 
                              className="p-6 cursor-pointer hover:bg-charcoal/80 transition-colors"
                              onClick={() => setExpandedSession(expandedSession === booking.id ? null : booking.id)}
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-slate-400" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold">Client Session</h3>
                                    <p className="text-sm text-slate-500">
                                      {new Date(booking.scheduled_date).toLocaleDateString('en-US', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      })} • {booking.scheduled_time}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="text-gold-500 font-semibold">${booking.amount}</p>
                                    <p className="text-xs text-slate-500">
                                      Earned: ${(booking.amount * 0.8).toFixed(0)}
                                    </p>
                                  </div>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor('completed')}`}>
                                    Completed
                                  </span>
                                  {expandedSession === booking.id ? (
                                    <ChevronUp className="w-5 h-5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {expandedSession === booking.id && (
                              <div className="border-t border-slate-800 p-6 bg-obsidian/50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                  <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Duration</p>
                                      <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <DollarSign className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Your Earnings</p>
                                      <p className="font-medium">${(booking.amount * 0.8).toFixed(0)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Users className="w-5 h-5 text-gold-500" />
                                    <div>
                                      <p className="text-xs text-slate-500">Platform Fee</p>
                                      <p className="font-medium">20% (${(booking.amount * 0.2).toFixed(0)})</p>
                                    </div>
                                  </div>
                                </div>

                                {booking.notes && (
                                  <div className="mb-6 p-4 bg-charcoal rounded-sm">
                                    <p className="text-xs text-slate-500 mb-1">Session Topic</p>
                                    <p className="text-sm text-slate-300">{booking.notes}</p>
                                  </div>
                                )}

                                {/* Download Buttons */}
                                <div className="flex flex-wrap gap-3">
                                  <Button
                                    variant="outline"
                                    className="border-slate-700 hover:bg-slate-800"
                                    onClick={() => handleDownloadRecording(booking.id)}
                                    disabled={downloadingRecording === booking.id}
                                  >
                                    {downloadingRecording === booking.id ? (
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                      <Play className="w-4 h-4 mr-2" />
                                    )}
                                    Download Recording
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="border-slate-700 hover:bg-slate-800"
                                    onClick={() => handleDownloadTranscript(booking.id)}
                                    disabled={downloadingTranscript === booking.id}
                                  >
                                    {downloadingTranscript === booking.id ? (
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                      <FileText className="w-4 h-4 mr-2" />
                                    )}
                                    Download Transcript
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-charcoal border border-slate-800 p-12 rounded-sm text-center">
                        <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">No past sessions yet</p>
                        <p className="text-sm text-slate-500 mt-2">Completed sessions will appear here</p>
                      </div>
                    )}
                  </>
                )}
              </motion.div>

              {/* Slot Requests Section */}
              <motion.div
                id="slot-requests-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden"
              >
                <div className="p-6 border-b border-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-gold-500" />
                      <h2 className="font-display text-xl">Slot Requests</h2>
                      {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length > 0 && (
                        <span className="bg-gold-500 text-obsidian text-xs font-bold px-2 py-1 rounded-full">
                          {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length} new
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Users requesting time slots from you</p>
                </div>

                <div className="divide-y divide-slate-800">
                  {loadingRequests ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-gold-500 mx-auto" />
                    </div>
                  ) : slotRequests.length === 0 ? (
                    <div className="p-12 text-center">
                      <CalendarPlus className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No slot requests yet</p>
                      <p className="text-sm text-slate-500 mt-2">When users request time slots, they'll appear here</p>
                    </div>
                  ) : (
                    slotRequests.map((request) => {
                      const statusBadge = getStatusBadge(request.status);
                      return (
                        <div key={request.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                  <User className="w-5 h-5 text-slate-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-white">{request.user_name}</p>
                                  <p className="text-sm text-slate-500">{request.user_email}</p>
                                </div>
                              </div>
                              <div className="ml-13 space-y-1">
                                <p className="text-sm text-slate-400">
                                  <span className="text-slate-500">Requested:</span>{" "}
                                  {request.timeframe === '1_week' ? 'Within next week' : 'Within next 2 weeks'}
                                </p>
                                <p className="text-sm text-slate-400">
                                  <span className="text-slate-500">Duration:</span>{" "}
                                  {request.duration_minutes} minutes
                                </p>
                                <p className="text-sm text-slate-400">
                                  <span className="text-slate-500">Timezone:</span>{" "}
                                  {request.user_timezone}
                                </p>
                                {request.message && (
                                  <p className="text-sm text-slate-400 italic">"{request.message}"</p>
                                )}
                                {request.user_message && (
                                  <p className="text-sm text-orange-400 mt-2">
                                    <span className="text-slate-500">User's response:</span> "{request.user_message}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(request.created_at).toLocaleDateString()}
                              </span>
                              {(request.status === 'pending' || request.status === 'more_requested') && (
                                <Button
                                  size="sm"
                                  className="bg-gold-500 hover:bg-gold-600 text-obsidian mt-2"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setProposedSlots([]);
                                    setExpertMessage("");
                                    setSelectedProposalDate(null);
                                    // Auto-detect expert's timezone
                                    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                    const tzInList = TIMEZONES.find(tz => tz.value === userTz);
                                    setSelectedTimezone(tzInList ? userTz : "UTC");
                                  }}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Propose Slots
                                </Button>
                              )}
                              {request.status === 'slots_proposed' && request.proposed_slots && (
                                <div className="text-right mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Proposed:</p>
                                  {request.proposed_slots.slice(0, 2).map((slot, i) => (
                                    <p key={i} className="text-xs text-blue-400">
                                      {slot.date} {slot.time_12h}
                                    </p>
                                  ))}
                                  {request.proposed_slots.length > 2 && (
                                    <p className="text-xs text-slate-500">+{request.proposed_slots.length - 2} more</p>
                                  )}
                                </div>
                              )}
                              {request.status === 'accepted' && request.accepted_slot && (
                                <div className="text-right mt-2">
                                  <p className="text-xs text-green-500 mb-1">Accepted:</p>
                                  <p className="text-xs text-green-400">
                                    {request.accepted_slot.date} {request.accepted_slot.time_12h}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>

              {/* Propose Slots Modal */}
              {selectedRequest && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-charcoal border border-slate-700 rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto"
                  >
                    <div className="p-6 border-b border-slate-700">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-xl">Propose Time Slots</h3>
                        <button
                          onClick={() => {
                            setSelectedRequest(null);
                            setSelectedProposalDate(null);
                          }}
                          className="text-slate-400 hover:text-white"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        For {selectedRequest.user_name} • {selectedRequest.duration_minutes} min session
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        User's timezone: {selectedRequest.user_timezone}
                      </p>
                    </div>

                    <div className="p-6 space-y-5">
                      {/* Step 1: Select Timezone */}
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-gold-500 text-obsidian text-xs flex items-center justify-center font-bold">1</span>
                          Select Your Timezone
                        </label>
                        <select
                          value={selectedTimezone}
                          onChange={(e) => setSelectedTimezone(e.target.value)}
                          className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-gold-500 focus:outline-none"
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Step 2: Select Date */}
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-gold-500 text-obsidian text-xs flex items-center justify-center font-bold">2</span>
                          Select Date
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                          {generateAvailableDates().map((dateObj, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedProposalDate(dateObj.date)}
                              className={`p-2 text-sm rounded border transition-colors ${
                                selectedProposalDate === dateObj.date
                                  ? 'bg-gold-500/20 border-gold-500 text-gold-500'
                                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-gold-500/50'
                              }`}
                            >
                              <div className="font-medium">{dateObj.displayDate}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Step 3: Select Time Slots */}
                      {selectedProposalDate && (
                        <div>
                          <label className="text-sm font-medium text-slate-300 mb-2 block flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-gold-500 text-obsidian text-xs flex items-center justify-center font-bold">3</span>
                            Select Time Slots for {new Date(selectedProposalDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </label>
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {generateTimeSlotsForDate(selectedProposalDate).map((slot, i) => {
                              const isSelected = proposedSlots.find(s => s.date === slot.date && s.time === slot.time);
                              return (
                                <button
                                  key={i}
                                  onClick={() => isSelected 
                                    ? setProposedSlots(proposedSlots.filter(s => !(s.date === slot.date && s.time === slot.time)))
                                    : addProposedSlot(slot.date, slot.time, slot.time_12h)
                                  }
                                  className={`p-2 text-sm rounded border transition-colors ${
                                    isSelected
                                      ? 'bg-gold-500/20 border-gold-500 text-gold-500'
                                      : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-gold-500/50'
                                  }`}
                                >
                                  {slot.time_12h}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Times shown in {TIMEZONES.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                          </p>
                        </div>
                      )}

                      {/* Selected slots preview */}
                      {proposedSlots.length > 0 && (
                        <div className="bg-slate-800/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slate-300">
                              Selected Slots ({proposedSlots.length}/6)
                            </label>
                            {proposedSlots.length >= 6 && (
                              <span className="text-xs text-orange-400">Maximum reached</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {proposedSlots.map((slot, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-gold-500/20 text-gold-500 text-sm rounded-full"
                              >
                                {slot.date} @ {slot.time_12h}
                                <button onClick={() => removeProposedSlot(i)} className="hover:text-white ml-1">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Message to user */}
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">
                          Message to User (optional)
                        </label>
                        <Textarea
                          value={expertMessage}
                          onChange={(e) => setExpertMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          className="bg-slate-800 border-slate-700"
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="p-6 border-t border-slate-700 flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSelectedRequest(null);
                          setSelectedProposalDate(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-gold-500 hover:bg-gold-600 text-obsidian"
                        onClick={() => handleProposeSlots(selectedRequest.id)}
                        disabled={proposingSlots || proposedSlots.length === 0}
                      >
                        {proposingSlots ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send {proposedSlots.length} Slot{proposedSlots.length !== 1 ? 's' : ''}
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Cancel Booking Modal */}
      <Dialog open={!!cancelModal} onOpenChange={() => { setCancelModal(null); setCancelReason(""); }}>
        <DialogContent className="bg-charcoal border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancel Session
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Cancelling this session will issue a full refund to the client.
            </DialogDescription>
          </DialogHeader>
          
          {cancelModal && (
            <div className="space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-white font-medium">{cancelModal.user_name}</p>
                <p className="text-slate-400 text-sm">
                  {cancelModal.scheduled_date} at {cancelModal.scheduled_time}
                </p>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-500 font-medium mb-1">Full Refund Policy</p>
                <p className="text-slate-300 text-sm">
                  When you cancel a session, the client receives a 100% refund (${cancelModal.amount?.toFixed(2)})
                </p>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Reason for cancellation (optional)</label>
                <Textarea
                  placeholder="Let the client know why you're cancelling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setCancelModal(null); setCancelReason(""); }}
              className="border-slate-600"
            >
              Keep Session
            </Button>
            <Button
              onClick={handleCancelBooking}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel & Refund"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Propose Reschedule Slots Modal */}
      <Dialog open={!!rescheduleModal} onOpenChange={() => { setRescheduleModal(null); setRescheduleSlots([]); setRescheduleMessage(""); setRescheduleDate(null); }}>
        <DialogContent className="bg-charcoal border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Propose New Time Slots</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select up to 6 alternative time slots for {rescheduleModal?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          {rescheduleModal && (
            <div className="space-y-4">
              {/* Current session info */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-400">Current Session</p>
                <p className="text-white">
                  {rescheduleModal.scheduled_date} at {rescheduleModal.scheduled_time}
                </p>
              </div>
              
              {/* Date selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Select Date</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {generateAvailableDates().map((dateInfo) => (
                    <button
                      key={dateInfo.date}
                      onClick={() => setRescheduleDate(dateInfo.date)}
                      className={`p-2 text-center rounded border transition-colors ${
                        rescheduleDate === dateInfo.date
                          ? 'bg-gold-500/20 border-gold-500 text-gold-500'
                          : 'bg-slate-800 border-slate-700 hover:border-gold-500/50'
                      }`}
                    >
                      <div className="text-xs text-slate-400">{dateInfo.dayName.slice(0, 3)}</div>
                      <div className="font-medium">{dateInfo.displayDate.split(',')[0].split(' ')[1]}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Time slots */}
              {rescheduleDate && (
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Select Time Slots</label>
                  <div className="grid grid-cols-4 gap-2">
                    {generateTimeSlotsForDate(rescheduleDate).map((slot) => {
                      const isSelected = rescheduleSlots.find(s => s.date === slot.date && s.time === slot.time);
                      return (
                        <button
                          key={`${slot.date}-${slot.time}`}
                          onClick={() => isSelected 
                            ? setRescheduleSlots(rescheduleSlots.filter(s => !(s.date === slot.date && s.time === slot.time)))
                            : addRescheduleSlot(slot.date, slot.time, slot.time_12h)
                          }
                          className={`p-2 text-sm rounded border transition-colors ${
                            isSelected
                              ? 'bg-gold-500/20 border-gold-500 text-gold-500'
                              : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-gold-500/50'
                          }`}
                        >
                          {slot.time_12h}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Selected slots preview */}
              {rescheduleSlots.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Selected Slots ({rescheduleSlots.length}/6)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {rescheduleSlots.map((slot, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-gold-500/20 text-gold-500 text-sm rounded-full"
                      >
                        {slot.date} @ {slot.time_12h}
                        <button onClick={() => removeRescheduleSlot(i)} className="hover:text-white ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Message */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Message (optional)</label>
                <Textarea
                  value={rescheduleMessage}
                  onChange={(e) => setRescheduleMessage(e.target.value)}
                  placeholder="Add a message explaining the reschedule..."
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setRescheduleModal(null); setRescheduleSlots([]); setRescheduleMessage(""); setRescheduleDate(null); }}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleProposeRescheduleSlots}
              disabled={proposingReschedule || rescheduleSlots.length === 0}
              className="bg-gold-500 hover:bg-gold-400 text-black"
              data-testid="send-reschedule-slots-btn"
            >
              {proposingReschedule ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Slots
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
